// @ts-strict-ignore
import type { WorkflowTestingSuiteState } from './state';
import { initialState } from './state';
import type { WhatIfPreReqs } from './types';

/* ==== Shared ==== */
export function setIsTestingSuiteVisible(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['isVisible'] },
) {
  state.shared.isVisible = action.payload;
}

export function resetTestingSuite() {
  /**
   * returning a value from a reducer replaces the whole root state (scoped to what-if).
   */
  return initialState;
}

export function setCurrentPage(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['currentPage'] },
) {
  state.shared.currentPage = action.payload;
}

export function setDataType(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['dataType'] },
) {
  state.shared.dataType = action.payload;
}

export function setPreviewChangesData(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['workflowChanges'] },
) {
  state.shared.workflowChanges = action.payload;
}

export function setTestName(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['testName'] },
) {
  state.shared.testName = action.payload;
}

export function setTestNotes(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['testNotes'] },
) {
  state.shared.testNotes = action.payload;
}

export function setVersionToTestAgainst(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['versionToTestAgainst'] },
) {
  state.shared.versionToTestAgainst = action.payload;
}

export function setVersionToTest(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['versionToTest'] },
) {
  state.shared.versionToTest = action.payload;
}

export function setWorkflowToken(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['workflowToken'] },
) {
  state.shared.workflowToken = action.payload;
}

export function setWorkflowType(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['workflowType'] },
) {
  state.shared.workflowType = action.payload;
}

export function setWorkflowName(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['workflowName'] },
) {
  state.shared.workflowName = action.payload;
}

export function setVersionNotes(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['versionNotes'] },
) {
  state.shared.versionNotes = action.payload;
}

export function setIsSubmittingTest(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['shared']['isSubmittingTest'] },
) {
  state.shared.isSubmittingTest = action.payload;
}

/* ==== What If ==== */
export function setWillCreateNewEvals(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['willCreateNewEvals'] },
) {
  state.whatIf.willCreateNewEvals = action.payload;
}

export function setNewEvalsModalOpen(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['newEvalsModal']['open'] },
) {
  state.whatIf.newEvalsModal.open = action.payload;
}

export function setNewEvalsModalConfirmed(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['newEvalsModal']['confirmed'] },
) {
  state.whatIf.newEvalsModal.confirmed = action.payload;
}

export function setIsFetching(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['isFetching'] },
) {
  state.whatIf.isFetching = action.payload;
}

export function setIsHistoricalDataAvailable(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['isHistoricalDataAvailable'] },
) {
  state.whatIf.isHistoricalDataAvailable = action.payload;
}

export function setWhatIfAnalysisToken(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['analysisToken'] },
) {
  state.whatIf.analysisToken = action.payload;
}

export function setWhatIfPrerequisites(
  state: WorkflowTestingSuiteState,
  action: { payload: WhatIfPreReqs },
) {
  state.whatIf.prerequisites = action.payload;
}

/* ==== What If Filters ==== */
export function setCreatedAtFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['createdAt'] },
) {
  state.whatIf.filters.createdAt = action.payload;
}

export function setFilteredEvaluationsSampleSize(
  state: WorkflowTestingSuiteState,
  action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['filteredEvaluationsSampleSize'] },
) {
  state.whatIf.filters.filteredEvaluationsSampleSize = action.payload;
}

export function setEvaluationTokensFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['evaluationTokens'] },
) {
  state.whatIf.filters.evaluationTokens = action.payload;
}

export function setOutcomeFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['outcome'] },
) {
  state.whatIf.filters.outcome = action.payload;
}

export function setTagsFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['tags'] },
) {
  state.whatIf.filters.tags = action.payload;
}

export function setReviewReasonFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['reviewReason'] },
) {
  state.whatIf.filters.reviewReason = action.payload;
}

export function setReasonCodeFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['reasonCode'] },
) {
  state.whatIf.filters.reasonCode = action.payload;
}

export function setFraudScoreFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['fraudScore'] },
) {
  state.whatIf.filters.fraudScore = action.payload;
}

export function setSampleSizeFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['sampleSize'] },
) {
  state.whatIf.filters.sampleSize = action.payload;
}

export function setSampleTypeFilter(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['filters']['sampleType'] },
) {
  state.whatIf.filters.sampleType = action.payload;
}

/* ==== What If Errors ==== */
export function setCreatedAtError(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['errors']['createdAt'] },
) {
  state.whatIf.errors.createdAt = action.payload;
}

export function setEvaluationTokenError(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['whatIf']['errors']['evaluationTokens'] },
) {
  state.whatIf.errors.evaluationTokens = action.payload;
}

/* ==== Champion Challenger ==== */
export function setDisplayVersion(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['championChallenger']['displayVersion'] },
) {
  state.championChallenger.displayVersion = action.payload;
}

export function setChampionChallengerModalOpen(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['championChallenger']['isModalOpen'] },
) {
  state.championChallenger.isModalOpen = action.payload;
}

export function setWillCreateNewWorkflowVersion(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['championChallenger']['willCreateNewWorkflowVersion'] },
) {
  state.championChallenger.willCreateNewWorkflowVersion = action.payload;
}

/* ==== PE Backtest ==== */
export function setAsOfTime(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['peBacktest']['asOfTime'] },
) {
  state.peBacktest.asOfTime = action.payload;
}

export function setTestSample(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['peBacktest']['testSample'] },
) {
  state.peBacktest.testSample = action.payload;
}

export function setSampleSize(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['peBacktest']['sampleSize'] },
) {
  state.peBacktest.sampleSize = action.payload;
}

/* ==== RT Backtest ==== */
export function setRealtimeSampleSize(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['rtBacktest']['sampleSize'] },
) {
  state.rtBacktest.sampleSize = action.payload;
}

export function setTotalSampleSize(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['rtBacktest']['totalSize'] },
) {
  state.rtBacktest.totalSize = action.payload;
}

export function setFilterParams(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['rtBacktest']['filterParams'] },
) {
  state.rtBacktest.filterParams = action.payload;
}

export function setTestToken(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['rtBacktest']['testToken'] },
) {
  state.rtBacktest.testToken = action.payload;
}

export function setHasTokenFormatError(
  state: WorkflowTestingSuiteState, action: { payload: WorkflowTestingSuiteState['rtBacktest']['hasTokenFormatError'] },
) {
  state.rtBacktest.hasTokenFormatError = action.payload;
}

